import React, { useEffect, useState, useCallback } from 'react';
import { Card } from 'react-bootstrap';
import { Button, Checkbox, CircularProgress, FormControlLabel } from '@material-ui/core';
import { Toast } from 'components/shared/Toaster';
import { getMyFlaggedBusinessTypes, updateMyFlaggedBusinessTypes } from 'actions/client';
import { businessTypeOption } from 'constants/constantVariables';
import AuthService from 'tools/AuthService';
import styles from '../profile.module.scss';

/**
 * Business types the logged-in user's own organization wants flagged in its application list.
 * Like SecurityTab, the endpoints resolve the client from the token, so this tab is only
 * rendered for a client admin.
 */
const FlaggedBusinessTypesTab = () => {
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [selected, setSelected] = useState([]);
    const [savedValue, setSavedValue] = useState([]);
    // Until the saved list has loaded, editing would start from an empty list and a save would wipe it
    const [loadFailed, setLoadFailed] = useState(false);
    // Everyone who sees the tab can read the list; only MANAGE_FLAGGING (customer admins) can change it
    const canManage = AuthService.hasPrivilege('MANAGE_FLAGGING');

    const isDirty = selected.length !== savedValue.length || selected.some(type => !savedValue.includes(type));

    const fetchSettings = useCallback(() => {
        setLoading(true);
        setLoadFailed(false);
        getMyFlaggedBusinessTypes()
            .then((res) => {
                const value = res?.businessTypes || [];
                setSelected(value);
                setSavedValue(value);
            })
            // request() already surfaced the failure as a toast
            .catch(() => setLoadFailed(true))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => {
        fetchSettings();
    }, [fetchSettings]);

    const toggle = (type) => {
        setSelected(current => current.includes(type)
            ? current.filter(t => t !== type)
            : [...current, type]);
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        setSaving(true);
        updateMyFlaggedBusinessTypes({ businessTypes: selected })
            .then((res) => {
                const value = res?.businessTypes || [];
                setSelected(value);
                setSavedValue(value);
                Toast({ type: 'success', message: 'Flagged business types saved' });
            })
            .catch(() => {})
            .finally(() => setSaving(false));
    };

    if (loading) {
        return (
            <Card className="p-4 text-center">
                <CircularProgress />
            </Card>
        );
    }

    if (loadFailed) {
        return (
            <Card className="p-4">
                <p className="mb-3" style={{ fontSize: 14 }}>Couldn't load your flagged business types.</p>
                <Button variant="outlined" color="primary" onClick={fetchSettings}>Try again</Button>
            </Card>
        );
    }

    return (
        <Card>
            <div className={styles.profileContainer}>
                <div className={styles.profileHeader}>
                    <h5>Flagging</h5>
                </div>

                <form onSubmit={handleSubmit}>
                    <h6 className="mt-3 mb-1">Business types</h6>
                    <p className="mb-2" style={{ fontSize: 14, color: 'rgba(0,0,0,0.59)', maxWidth: '46rem' }}>
                        Applications with one of these business types are flagged in the application list,
                        on the application page, and when approving.
                    </p>

                    <div className="d-flex flex-column mb-4">
                        {businessTypeOption.map(({ label, value }) => (
                            <FormControlLabel
                                key={value}
                                label={label}
                                control={
                                    <Checkbox
                                        size="small"
                                        color="primary"
                                        checked={selected.includes(value)}
                                        onChange={() => toggle(value)}
                                        disabled={!canManage || saving}
                                    />
                                }
                            />
                        ))}
                    </div>

                    {canManage ? (
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            disabled={!isDirty || saving}
                        >
                            {saving ? <CircularProgress size={22} color="inherit" /> : 'Save'}
                        </Button>
                    ) : (
                        <p className="mb-0" style={{ fontSize: 14, color: 'rgba(0,0,0,0.59)' }}>
                            Only customer admins can change flagged business types.
                        </p>
                    )}
                </form>
            </div>
        </Card>
    );
};

export default FlaggedBusinessTypesTab;
